import { AxiosRequestConfig } from "axios";
import { createOrUpdateSupplierProduct, updateCurrency, createOrUpdateSupplier, createOrUpdateReturnOrder, createOrUpdatePurchaseOrderDraft, addReturnOrderRow, editManufacturers, createOrUpdateCategories, deleteCategories, createOrUpdateProductLine, createOrUpdateLowOrderPoints, createOrUpdateTransferOrder, createOrUpdatePurchaseOrder, createReturnOrderFromOrder, createReplacementOrder, returnToCustomer, returnToWarehouse, scrapReturnOrderItems } from "src/core/api/apiRequests";
import { buildSubmitRequest, filterValidRequests } from "./buildSubmitRequest";
import { toReturnPayload, toCurrencyPayload, toSupplierProductPayload, toSupplierProductUpdatePayload, toSupplierPayload, toPODraftUpdatePayload, toPOOrderDraftPayload, mapProductToReturnRow, toManufacturersPayload, toCategoriesPayload, mapTableDataToCategoriesDelete, toProductLinePayload, toLowOrderPointPayload, toCategoriesUpdatePayload, toTranferOrderPayload, toPOOrderPayload, mapCreateReturnOrderFromOrder, mapReturnToWarehousePayload, mapReplacementOrderPayload, mapReturnToCustomerPayload, mapScrapItemsPayload } from "./mappings";
import type { CurrencyUpdateValues } from 'src/core/forms/submit/mappings';

export const buildCurrencyRequest = (values: CurrencyUpdateValues): AxiosRequestConfig => {
  const id = values.Currency?.Key;
  if (!Number.isSafeInteger(id) || id <= 0) throw new Error('Select an existing currency to edit.');
  const payload = toCurrencyPayload(values);
  if (typeof payload.Description !== 'string') throw new Error('Enter a currency description.');
  if (payload.FixedRate !== null && !Number.isFinite(payload.FixedRate)) throw new Error('Enter a valid fixed rate or leave it empty.');
  return updateCurrency(payload, id);
};

// ---------------------------------
// RETURNS
// ---------------------------------
export const buildReturnsRequest = (values: any): AxiosRequestConfig[] => {
  const formValues = values?.values;
  const newProduct = values?.newProduct;

  const returnOrderId = formValues?.ReturnOrderId ?? null;
  const salesOrderId = formValues?.SalesOrderId ?? null;

  // ---------------------------------
  // CREATE-FROM-SALES-ORDER FLOW
  // ---------------------------------
  if (!returnOrderId && salesOrderId) {
    const requests = [
      buildSubmitRequest({
        values: formValues,
        createOrUpdateFn: createReturnOrderFromOrder,
        mapCreate: mapCreateReturnOrderFromOrder,
        idField: "SalesOrderId",
        updateMethod: "POST",
      }),
    ];

    return filterValidRequests(requests);
  }

  // ---------------------------------
  // EDIT FLOW (ReturnOrderId exists)
  // ---------------------------------
  const addedRows = (formValues?.ReturnOrderRows ?? []).filter((row: any) => Number(row.Id) < 0);
  const baseRequests = [
    ...addedRows.map((row: any) => addReturnOrderRow({ ArticleNumber: row.ArticleNumber, RowTypeId: row.RowTypeId ?? 1, QuantityToReturn: row.QuantityToReturn, Comment: row.Comment ?? '' }, 'POST', returnOrderId)),
    buildSubmitRequest({
      values: { ...formValues, newProduct },
      createOrUpdateFn: addReturnOrderRow,
      mapCreate: mapProductToReturnRow,
      idField: "ReturnOrderId",
      updateMethod: "POST",
    }),
    buildSubmitRequest({
      values: formValues,
      createOrUpdateFn: createOrUpdateReturnOrder,
      mapCreate: toReturnPayload,
      idField: "ReturnOrderId",
      updateMethod: "PUT",
    }),
  ];

  // If you somehow don’t have an id, we cannot call row action endpoints
  console.log("RETURN ORDER ID", returnOrderId);
  if (!returnOrderId) return filterValidRequests(baseRequests);

  const actionRequests = [
    // Return to warehouse
    buildSubmitRequest({
      values: withOnlyActionRows(formValues, "Return to warehouse"),
      createOrUpdateFn: returnToWarehouse,
      mapCreate: mapReturnToWarehousePayload,
      idField: "ReturnOrderId",
      updateMethod: "POST",
    }),

    // Return to customer
    buildSubmitRequest({
      values: withOnlyActionRows(formValues, "Return to customer"),
      createOrUpdateFn: returnToCustomer,
      mapCreate: mapReturnToCustomerPayload,
      idField: "ReturnOrderId",
      updateMethod: "POST",
    }),

    // Scrap
    buildSubmitRequest({
      values: withOnlyActionRows(formValues, "Scrap"),
      createOrUpdateFn: scrapReturnOrderItems,
      mapCreate: mapScrapItemsPayload,
      idField: "ReturnOrderId",
      updateMethod: "POST",
    }),

    // Replacement order
    buildSubmitRequest({
      values: withOnlyActionRows(formValues, "Create replacement order"),
      createOrUpdateFn: createReplacementOrder,
      mapCreate: mapReplacementOrderPayload,
      idField: "ReturnOrderId",
      updateMethod: "POST",
    }),
  ];

  return filterValidRequests([
    ...baseRequests,
    ...actionRequests,
  ]);
};

const withOnlyActionRows = (formValues: any, actionName: string) => {
  const rows = (formValues?.ReturnOrderRows ?? []).filter((r: any) => String(r?.ActionName ?? "") === actionName);
  return { ...formValues, ReturnOrderRows: rows };
};

const hasActionableRows = (formValues: any) => {
  const rows = formValues?.ReturnOrderRows ?? [];
  return rows.some((r: any) => Number(r?.Id) > 0 && Number(r?.QuantityToReturn ?? 0) > 0);
};

// ---------------------------------
// SUPPLIER PRODUCTS
// ---------------------------------
export const buildSupplierProductRequest = (values: any): AxiosRequestConfig => {
  const request = buildSubmitRequest({
    values,
    createOrUpdateFn: createOrUpdateSupplierProduct,
    mapCreate: toSupplierProductPayload,
    mapUpdate: toSupplierProductUpdatePayload,
    idField: "SupplierAssortmentItemId",
  })!;
  //console.log("buildSupplierProductRequest", request);
  return request;
};

// ---------------------------------
// SUPPLIERS
// ---------------------------------
export const buildSupplierRequest = (values: any): AxiosRequestConfig => {
  return buildSubmitRequest({
    values,
    createOrUpdateFn: createOrUpdateSupplier,
    mapCreate: toSupplierPayload,
  })!;
};

// ---------------------------------
// MANUFACTURERS
// ---------------------------------
export const buildManufacturerRequest = (values: any): AxiosRequestConfig[] => {
  const requests = [
    buildSubmitRequest({
      values,
      createOrUpdateFn: editManufacturers,
      mapCreate: toManufacturersPayload,
    }),
    buildSubmitRequest({
      values,
      createOrUpdateFn: editManufacturers,
      mapCreate: toManufacturersPayload,
      isDelete: true,
    }),
  ];
  return filterValidRequests(requests);
};

// ---------------------------------
// PRODUCT LINES
// ---------------------------------
export const buildProductLinesRequest = (values: any): AxiosRequestConfig => {
  return buildSubmitRequest({
    values,
    createOrUpdateFn: createOrUpdateProductLine,
    mapCreate: toProductLinePayload,
  })!;
};

// ---------------------------------
// CATEGORIES
// ---------------------------------
export const buildCategoriesRequest = (values: any, languageId: number): AxiosRequestConfig[] => {
  //console.log("buildCategoriesRequest", values);
  const requests = [
    buildSubmitRequest({
      values,
      createOrUpdateFn: createOrUpdateCategories,
      mapCreate: toCategoriesPayload,
      idArgOverride: languageId,
    }),
    buildSubmitRequest({
      values,
      createOrUpdateFn: createOrUpdateCategories,
      mapCreate: toCategoriesUpdatePayload,
      methodOverride: "PATCH",
      idArgOverride: languageId,
    }),
    buildSubmitRequest({
      values,
      createOrUpdateFn: deleteCategories,
      mapCreate: mapTableDataToCategoriesDelete,
      isDelete: true
      // deleteCategories signature remains unchanged; no override needed
    }),
  ];
  // console.log("buildCategoriesRequest", requests);
  return filterValidRequests(requests);
};

// ---------------------------------
// PURCHASING
// ---------------------------------
export const buildPurchaseOrderRequest = (values: any): AxiosRequestConfig => {
  return buildSubmitRequest({
    values,
    createOrUpdateFn: createOrUpdatePurchaseOrder,
    mapCreate: toPOOrderPayload,
  })!;
};

// ---------------------------------
// PURCHASE ORDER DRAFTS
// ---------------------------------
export const buildPurchaseOrderDraftRequest = (values: any): AxiosRequestConfig => {
  return buildSubmitRequest({
    values,
    createOrUpdateFn: createOrUpdatePurchaseOrderDraft,
    mapCreate: toPOOrderDraftPayload,
    mapUpdate: toPODraftUpdatePayload,
    idField: "PurchaseOrderDraftId",
  })!;
};

// ---------------------------------
// LOW ORDER POINTS
// ---------------------------------
export const lowOrderPointsPatchRequest = (values: any): AxiosRequestConfig => {
  const item = toLowOrderPointPayload(values);
  return createOrUpdateLowOrderPoints(item, "PATCH", values.Id);
};

export const buildLowOrderPointRequest = (values: any): AxiosRequestConfig => {
  return buildSubmitRequest({
    values,
    createOrUpdateFn: createOrUpdateLowOrderPoints,
    mapCreate: toLowOrderPointPayload,
  });
};

// ---------------------------------
// TRANSFER ORDERS
// ---------------------------------
export const buildTransferOrderRequest = (values: any): AxiosRequestConfig => {
  return buildSubmitRequest({
    values,
    createOrUpdateFn: createOrUpdateTransferOrder,
    mapCreate: toTranferOrderPayload,
    idField: "OrderId",
  });
};

export { filterValidRequests };
